// A7 - Logistics, weapons, fuel, supplies mocks

export type WarehouseType = 'weapons' | 'ammunition' | 'fuel' | 'spare_parts' | 'uniforms' | 'general'
export type SecurityLevel = 'normal' | 'restricted' | 'high_security' | 'maximum'

export interface Warehouse {
  id: string
  code: string
  name: string
  type: WarehouseType
  unitCode: string
  unitName: string
  region: 'north' | 'central' | 'south'
  securityLevel: SecurityLevel
  custodian: string
  status: 'active' | 'maintenance' | 'decommissioned'
  capacityM2: number
  occupancyPct: number
  lon: number
  lat: number
}

export interface WeaponItem {
  id: string
  code: string
  name: string
  category: 'air-to-air' | 'air-to-ground' | 'gun' | 'bomb' | 'rocket' | 'flare'
  warehouseCode: string
  quantity: number
  unit: 'quả' | 'viên' | 'thùng'
  lotNumber: string
  manufactureDate: string
  expiryDate: string
  status: 'serviceable' | 'unserviceable' | 'expired' | 'reserved'
  classification: 'restricted' | 'secret'
  reorderPoint: number
  unitPriceM: number
}

export interface FuelTank {
  id: string
  code: string
  airportCode: string
  fuelType: 'Jet A-1' | 'JP-8' | 'Mogas-95' | 'Diesel'
  capacityKL: number
  currentKL: number
  temperatureC: number
  density: number
  lastQualityCheck: string
  qualityResult: 'pass' | 'pending' | 'fail'
  inflowKLh: number
  outflowKLh: number
  status: 'online' | 'maintenance' | 'offline'
}

export interface SparePart {
  id: string
  code: string
  pn: string
  name: string
  aircraftType: string
  category: 'engine' | 'avionics' | 'airframe' | 'landing-gear' | 'hydraulic' | 'tire'
  warehouseCode: string
  quantity: number
  reorderPoint: number
  leadTimeDays: number
  unitPriceK: number
  status: 'in-stock' | 'low-stock' | 'out-of-stock' | 'on-order'
}

export interface ConsumptionStandard {
  id: string
  itemCode: string
  itemName: string
  category: 'weapon' | 'fuel' | 'spare-part'
  perFlightHour: number
  unit: string
  monthlyQuota: number
  ytdConsumed: number
  ytdQuota: number
}

export interface InventoryAlert {
  id: string
  itemCode: string
  itemName: string
  warehouseCode: string
  level: 'critical' | 'warning' | 'info'
  current: number
  reorderPoint: number
  daysUntilStockout: number
  suggestedReorder: number
  createdAt: string
}

export interface StockTransaction {
  id: string
  ts: string
  kind: 'inbound' | 'outbound' | 'transfer' | 'consumption' | 'audit-adjust'
  itemCode: string
  itemName: string
  warehouseCode: string
  quantity: number
  unit: string
  operatorName: string
  reason: string
  flightPlanCode?: string
}

const WH_DATA: Array<[string, string, WarehouseType, string, 'north'|'central'|'south', SecurityLevel, number, number, number]> = [
  ['WH-NB-WPN-001', 'Kho vũ khí Nội Bài', 'weapons', 'SD-371', 'north', 'maximum', 8500, 78, 105.81],
  ['WH-NB-AMO-001', 'Kho đạn dược Nội Bài', 'ammunition', 'SD-371', 'north', 'maximum', 4200, 64, 105.80],
  ['WH-NB-FUE-001', 'Kho nhiên liệu HK Nội Bài', 'fuel', 'SD-371', 'north', 'high_security', 12000, 71, 105.82],
  ['WH-NB-SPR-001', 'Kho phụ tùng Nội Bài', 'spare_parts', 'SD-371', 'north', 'restricted', 6800, 55, 105.81],
  ['WH-DN-WPN-001', 'Kho vũ khí Đà Nẵng', 'weapons', 'SD-372', 'central', 'maximum', 6200, 82, 108.20],
  ['WH-DN-FUE-001', 'Kho nhiên liệu Đà Nẵng', 'fuel', 'SD-372', 'central', 'high_security', 9500, 68, 108.19],
  ['WH-DN-SPR-001', 'Kho phụ tùng Đà Nẵng', 'spare_parts', 'SD-372', 'central', 'restricted', 5400, 47, 108.20],
  ['WH-BH-WPN-001', 'Kho vũ khí Biên Hòa', 'weapons', 'SD-370', 'south', 'maximum', 7800, 75, 106.82],
  ['WH-BH-FUE-001', 'Kho nhiên liệu Biên Hòa', 'fuel', 'SD-370', 'south', 'high_security', 11200, 81, 106.81],
  ['WH-BH-SPR-001', 'Kho phụ tùng Biên Hòa', 'spare_parts', 'SD-370', 'south', 'restricted', 6100, 60, 106.82],
  ['WH-918-UNI-01', 'Kho quân trang Lữ 918', 'uniforms', 'L918', 'north', 'normal', 3200, 42, 105.78],
  ['WH-PK-PK-001', 'Kho TLPK Sư đoàn 361', 'weapons', 'SD-361', 'north', 'maximum', 5500, 88, 105.65],
]

export const WAREHOUSES: Warehouse[] = WH_DATA.map(([code, name, type, unitCode, region, sec, cap, occ, lon], i) => ({
  id: `wh-${i + 1}`,
  code,
  name,
  type,
  unitCode,
  unitName: unitCode,
  region,
  securityLevel: sec,
  custodian: ['Tr.tá Phạm V.A', 'Th.tá Nguyễn V.B', 'Đ.uý Lê V.C', 'Tr.uý Vũ V.D', 'Th.tá Trần V.E', 'Tr.t Hoàng V.F'][i % 6],
  status: i === 9 ? 'maintenance' : 'active',
  capacityM2: cap,
  occupancyPct: occ,
  lon,
  lat: region === 'north' ? 21.02 : region === 'central' ? 16.07 : 10.85,
}))

const WEAPON_DATA: Array<[string, string, WeaponItem['category'], string, number, WeaponItem['unit'], number]> = [
  ['R-77', 'Tên lửa không-đối-không R-77 (AA-12)', 'air-to-air', 'WH-NB-WPN-001', 24, 'quả', 4],
  ['R-73', 'Tên lửa không-đối-không R-73 (AA-11)', 'air-to-air', 'WH-NB-WPN-001', 36, 'quả', 6],
  ['Kh-29T', 'Tên lửa không-đối-đất Kh-29T', 'air-to-ground', 'WH-NB-WPN-001', 18, 'quả', 3],
  ['Kh-31A', 'Tên lửa Kh-31A (chống tàu)', 'air-to-ground', 'WH-DN-WPN-001', 12, 'quả', 2],
  ['FAB-250', 'Bom FAB-250 KG', 'bomb', 'WH-NB-WPN-001', 240, 'quả', 40],
  ['FAB-500', 'Bom FAB-500 KG', 'bomb', 'WH-DN-WPN-001', 120, 'quả', 20],
  ['S-8', 'Đạn rocket S-8', 'rocket', 'WH-BH-WPN-001', 1800, 'viên', 300],
  ['S-25', 'Đạn rocket S-25', 'rocket', 'WH-BH-WPN-001', 240, 'viên', 40],
  ['GSh-30-1', 'Đạn pháo 30mm GSh-30-1', 'gun', 'WH-NB-AMO-001', 24000, 'viên', 4000],
  ['Flare-PPI', 'Đạn nhiệt PPI', 'flare', 'WH-NB-AMO-001', 6800, 'viên', 1000],
  ['Chaff-RC', 'Đạn nhiễu Chaff', 'flare', 'WH-DN-WPN-001', 4200, 'viên', 600],
  ['9M311', 'Tên lửa Strela 9M311 (Tunguska)', 'air-to-ground', 'WH-PK-PK-001', 96, 'quả', 16],
  ['5V55', 'Tên lửa S-300 5V55', 'air-to-ground', 'WH-PK-PK-001', 32, 'quả', 4],
]

export const WEAPONS: WeaponItem[] = WEAPON_DATA.map(([code, name, cat, wh, qty, unit, rop], i) => ({
  id: `wpn-${i + 1}`,
  code,
  name,
  category: cat,
  warehouseCode: wh,
  quantity: qty,
  unit,
  lotNumber: `LOT-${2020 + (i % 5)}-${String(i + 1).padStart(3, '0')}`,
  manufactureDate: `${2020 + (i % 5)}-${String((i % 12) + 1).padStart(2, '0')}-15`,
  expiryDate: `${2026 + ((i + 2) % 4)}-${String((i % 12) + 1).padStart(2, '0')}-15`,
  status: i === 4 || i === 9 ? 'reserved' : i === 11 ? 'unserviceable' : 'serviceable',
  classification: cat === 'air-to-air' || cat === 'air-to-ground' ? 'secret' : 'restricted',
  reorderPoint: rop,
  unitPriceM: cat === 'air-to-air' ? 8 + i * 0.5 : cat === 'air-to-ground' ? 12 + i * 0.3 : cat === 'bomb' ? 0.05 + i * 0.01 : 0.001,
}))

const FUEL_DATA: Array<[string, string, FuelTank['fuelType'], number, number]> = [
  ['TANK-NB-01', 'NBA', 'Jet A-1', 2400, 1820],
  ['TANK-NB-02', 'NBA', 'Jet A-1', 2400, 1340],
  ['TANK-NB-03', 'NBA', 'JP-8', 1200, 980],
  ['TANK-DN-01', 'DAD', 'Jet A-1', 1800, 1280],
  ['TANK-DN-02', 'DAD', 'JP-8', 800, 540],
  ['TANK-BH-01', 'BHA', 'Jet A-1', 2200, 1900],
  ['TANK-BH-02', 'BHA', 'Jet A-1', 2200, 1740],
  ['TANK-BH-03', 'BHA', 'JP-8', 1100, 220],
  ['TANK-PT-01', 'PTH', 'Jet A-1', 1600, 1020],
  ['TANK-CB-01', 'CBA', 'Jet A-1', 1400, 880],
]

export const FUEL_TANKS: FuelTank[] = FUEL_DATA.map(([code, ap, type, cap, cur], i) => ({
  id: `tank-${i + 1}`,
  code,
  airportCode: ap,
  fuelType: type,
  capacityKL: cap,
  currentKL: cur,
  temperatureC: 28 + ((i * 7) % 8),
  density: 0.795 + ((i * 3) % 10) / 1000,
  lastQualityCheck: `2026-05-0${(i % 6) + 1}`,
  qualityResult: i === 7 ? 'fail' : i === 4 ? 'pending' : 'pass',
  inflowKLh: i % 3 === 0 ? 8 + (i % 5) : 0,
  outflowKLh: i % 2 === 0 ? 12 + (i % 7) : 4 + (i % 3),
  status: i === 7 ? 'maintenance' : 'online',
}))

const SP_DATA: Array<[string, string, string, string, SparePart['category'], string, number, number, number, number]> = [
  ['SP-EN-001', 'AL-31FP-CMP-A1', 'Cánh nén tầng 1 AL-31FP', 'Su-30MK2', 'engine', 'WH-NB-SPR-001', 4, 2, 90, 280],
  ['SP-EN-002', 'AL-31FP-TURB-B', 'Tua-bin AL-31FP cấp B', 'Su-30MK2', 'engine', 'WH-NB-SPR-001', 2, 2, 120, 450],
  ['SP-AV-001', 'N-001VEP-RAD', 'Radar N001VE-P', 'Su-30MK2', 'avionics', 'WH-NB-SPR-001', 1, 1, 180, 1200],
  ['SP-AV-002', 'KAI-7-MFCD', 'Multi-Function CRT Display', 'Su-30MK2', 'avionics', 'WH-DN-SPR-001', 6, 3, 60, 85],
  ['SP-AF-001', 'WG-SU30-PNL-23', 'Tấm vỏ cánh phải PNL-23', 'Su-30MK2', 'airframe', 'WH-BH-SPR-001', 3, 2, 75, 22],
  ['SP-LG-001', 'MLG-NB-STRUT', 'Càng đáp chính trái', 'Su-30MK2', 'landing-gear', 'WH-NB-SPR-001', 2, 1, 90, 65],
  ['SP-HY-001', 'HYD-PMP-MAIN', 'Bơm thuỷ lực chính', 'Su-30MK2', 'hydraulic', 'WH-NB-SPR-001', 5, 3, 45, 18],
  ['SP-TR-001', 'TIRE-MAIN-22', 'Lốp càng chính 22"', 'Su-30MK2', 'tire', 'WH-NB-SPR-001', 12, 6, 30, 4.2],
  ['SP-EN-003', 'TV3-117-CMP', 'Cánh nén TV3-117 (Mi-17)', 'Mi-17', 'engine', 'WH-NB-SPR-001', 3, 2, 80, 180],
  ['SP-AV-003', 'YAK-DCS', 'Cluster bay Yak-130', 'Yak-130', 'avionics', 'WH-DN-SPR-001', 4, 2, 50, 95],
  ['SP-AF-002', 'L39-CANOPY', 'Khung kính canopy L-39', 'L-39C', 'airframe', 'WH-DN-SPR-001', 2, 2, 60, 32],
  ['SP-TR-002', 'TIRE-NLG-18', 'Lốp càng mũi 18"', 'Su-30MK2', 'tire', 'WH-BH-SPR-001', 8, 4, 30, 3.4],
  ['SP-HY-002', 'HYD-RES-22L', 'Bể chứa thuỷ lực 22L', 'Su-30MK2', 'hydraulic', 'WH-NB-SPR-001', 0, 2, 60, 8.5],
  ['SP-EN-004', 'NOZZLE-AB-SU22', 'Loa AB Su-22', 'Su-22M4', 'engine', 'WH-NB-SPR-001', 2, 1, 120, 110],
]

export const SPARE_PARTS: SparePart[] = SP_DATA.map(([code, pn, name, ac, cat, wh, qty, rop, lt, price], i) => ({
  id: `sp-${i + 1}`,
  code,
  pn,
  name,
  aircraftType: ac,
  category: cat,
  warehouseCode: wh,
  quantity: qty,
  reorderPoint: rop,
  leadTimeDays: lt,
  unitPriceK: price,
  status: qty === 0 ? 'out-of-stock' : qty <= rop ? 'low-stock' : i === 12 ? 'on-order' : 'in-stock',
}))

export const CONSUMPTION_STANDARDS: ConsumptionStandard[] = [
  { id: 'cs-1', itemCode: 'JET-A1', itemName: 'Jet A-1', category: 'fuel', perFlightHour: 4500, unit: 'L', monthlyQuota: 1800000, ytdConsumed: 6840000, ytdQuota: 9000000 },
  { id: 'cs-2', itemCode: 'JP-8', itemName: 'JP-8', category: 'fuel', perFlightHour: 3200, unit: 'L', monthlyQuota: 800000, ytdConsumed: 3120000, ytdQuota: 4000000 },
  { id: 'cs-3', itemCode: 'GSh-30-1', itemName: 'Đạn 30mm', category: 'weapon', perFlightHour: 0, unit: 'viên', monthlyQuota: 800, ytdConsumed: 3200, ytdQuota: 4000 },
  { id: 'cs-4', itemCode: 'S-8', itemName: 'Rocket S-8', category: 'weapon', perFlightHour: 0, unit: 'viên', monthlyQuota: 60, ytdConsumed: 280, ytdQuota: 300 },
  { id: 'cs-5', itemCode: 'Flare-PPI', itemName: 'Flare PPI', category: 'weapon', perFlightHour: 2.4, unit: 'viên', monthlyQuota: 200, ytdConsumed: 920, ytdQuota: 1000 },
  { id: 'cs-6', itemCode: 'TIRE-MAIN-22', itemName: 'Lốp 22"', category: 'spare-part', perFlightHour: 0, unit: 'cái', monthlyQuota: 12, ytdConsumed: 48, ytdQuota: 60 },
]

export const INVENTORY_ALERTS: InventoryAlert[] = [
  { id: 'al-1', itemCode: 'TANK-BH-03', itemName: 'Tank JP-8 BHA-03', warehouseCode: 'WH-BH-FUE-001', level: 'critical', current: 220, reorderPoint: 400, daysUntilStockout: 4, suggestedReorder: 800, createdAt: '06:42' },
  { id: 'al-2', itemCode: 'SP-HY-002', itemName: 'Bể chứa thuỷ lực 22L', warehouseCode: 'WH-NB-SPR-001', level: 'critical', current: 0, reorderPoint: 2, daysUntilStockout: 0, suggestedReorder: 4, createdAt: '08:11' },
  { id: 'al-3', itemCode: 'Kh-29T', itemName: 'Tên lửa Kh-29T', warehouseCode: 'WH-NB-WPN-001', level: 'warning', current: 18, reorderPoint: 12, daysUntilStockout: 32, suggestedReorder: 12, createdAt: '07:38' },
  { id: 'al-4', itemCode: 'SP-EN-004', itemName: 'Loa AB Su-22', warehouseCode: 'WH-NB-SPR-001', level: 'warning', current: 2, reorderPoint: 1, daysUntilStockout: 22, suggestedReorder: 3, createdAt: '06:15' },
  { id: 'al-5', itemCode: 'SP-TR-002', itemName: 'Lốp càng mũi 18"', warehouseCode: 'WH-BH-SPR-001', level: 'info', current: 8, reorderPoint: 4, daysUntilStockout: 60, suggestedReorder: 0, createdAt: '05:30' },
  { id: 'al-6', itemCode: 'GSh-30-1', itemName: 'Đạn 30mm', warehouseCode: 'WH-NB-AMO-001', level: 'info', current: 24000, reorderPoint: 4000, daysUntilStockout: 90, suggestedReorder: 0, createdAt: '04:50' },
]

const TX_KINDS: StockTransaction['kind'][] = ['inbound', 'outbound', 'transfer', 'consumption']
export const STOCK_TRANSACTIONS: StockTransaction[] = Array.from({ length: 30 }, (_, i) => {
  const kind = TX_KINDS[i % 4]
  const items = [
    ['JET-A1', 'Jet A-1', 'L'],
    ['R-73', 'R-73', 'quả'],
    ['SP-TR-001', 'Lốp 22"', 'cái'],
    ['Flare-PPI', 'Flare PPI', 'viên'],
    ['GSh-30-1', 'Đạn 30mm', 'viên'],
    ['SP-HY-001', 'Bơm thuỷ lực', 'cái'],
  ]
  const item = items[i % items.length]
  const wh = WAREHOUSES[i % WAREHOUSES.length]
  return {
    id: `tx-${i + 1}`,
    ts: `2026-05-06 ${String(8 + Math.floor(i / 4)).padStart(2, '0')}:${String((i * 7) % 60).padStart(2, '0')}`,
    kind,
    itemCode: item[0],
    itemName: item[1],
    warehouseCode: wh.code,
    quantity: kind === 'inbound' ? 100 + i * 10 : -(50 + i * 5),
    unit: item[2],
    operatorName: ['Th.tá V.A', 'Đ.uý B.C', 'Tr.t D.E', 'Tr.uý F.G'][i % 4],
    reason: kind === 'consumption' ? 'Bay huấn luyện' : kind === 'inbound' ? 'Nhập từ Tổng cục Hậu cần' : kind === 'transfer' ? 'Chuyển giữa kho' : 'Cấp phát theo KHB',
    flightPlanCode: kind === 'consumption' ? `KHB-2026-${String(100 + i).padStart(4, '0')}` : undefined,
  }
})

export const LOGISTICS_KPIS = {
  fuelTotalKL: FUEL_TANKS.reduce((s, t) => s + t.currentKL, 0),
  fuelCapKL: FUEL_TANKS.reduce((s, t) => s + t.capacityKL, 0),
  weaponsServiceable: WEAPONS.filter((w) => w.status === 'serviceable').length,
  weaponsTotal: WEAPONS.length,
  sparePartsLowStock: SPARE_PARTS.filter((s) => s.status === 'low-stock' || s.status === 'out-of-stock').length,
  warehousesActive: WAREHOUSES.filter((w) => w.status === 'active').length,
  alertsCritical: INVENTORY_ALERTS.filter((a) => a.level === 'critical').length,
}
